import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoggingService } from '../../services/logging.service';
import { AppUser, AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';
import { UserTagCrudService } from '../../services/user-tag-crud.service';
import { Assignment } from '../../models/assignment';
import { User } from '../../models/user';
import { ManagerDashboardService } from '../../services/manager-dashboard.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserProfileService } from '../../services/user-profile.service';
import { TenantRouterService } from '../../services/tenant-router.service';
import { Role } from '../../models/role';


@Component({
  selector: 'app-user-dashboard',
  standalone: false,
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss'
})
export class UserDashboardComponent implements OnInit, OnDestroy {

  constructor(private router: Router, private loggingService: LoggingService,
    private authService: AuthService, private userTagCrudService: UserTagCrudService,
    private managerDashboardService: ManagerDashboardService, private fb: FormBuilder,
    private userProfileService: UserProfileService, private tenantRouter: TenantRouterService) {

  }
  userId !: number;
  assignmentListSubscription !: Subscription;
  roles: Role[] = [];
  lastAssignment?: Assignment;
  managerId !: number;
  userRole !: string;
  user: User = new User();
  userSubscription !: Subscription;
  profileForm!: FormGroup;
  isProfileCompleted = false;
  profileLoaded = false;


  ngOnInit(): void {
    this.loggingService.logDebug("Getting the userId from session storage");
    const userid = sessionStorage.getItem('user_id');
    if (userid) {
      this.userId = parseInt(userid, 10);
    }
    this.loggingService.logDebug("Getting the managerId from session storage");
    const managerid = sessionStorage.getItem('manager_id');
    if (managerid) {
      this.managerId = parseInt(managerid, 10);
    }
    this.loggingService.logDebug("Getting the userrole from session storage");
    const userrole = sessionStorage.getItem('user_role');
    if (userrole) {
      this.userRole = userrole;
    }
    this.buildProfileForm();
    this.loadUserProfile();


    this.assignmentListSubscription = this.userTagCrudService.getAllAssignment(this.userId)
      .subscribe((assignments) => {
        if (assignments && assignments.length > 0) {
          this.lastAssignment = assignments[assignments.length - 1];
        }
      });

    this.userSubscription = this.managerDashboardService.getAllDetails(this.managerId,
      this.userId, this.userRole).subscribe((user) => {
        this.user = user;
        const appUser: AppUser = {
          UserID: user.UserID,
          Name: user.Name,
          Email: user.Email,
          IsActive: user.IsActive,
          personType: this.mapPersonType(user.personType)
        };

        this.authService.setCurrentUser(appUser);
      });
  }

  loadAspirationTypes() {
    this.userProfileService.getAllRole().subscribe({
      next: (res: Role[]) => {
        this.roles = res;
      }
    });
  }

  private mapPersonType(value: string): 'user' | 'manager' | 'hr' {
    const allowedTypes = ['user', 'manager', 'hr'];
    return allowedTypes.includes(value) ? (value as 'user' | 'manager' | 'hr') : 'user';
  }

  buildProfileForm() {
    this.profileForm = this.fb.group({
      countryCode: ['+91', Validators.required],
      phoneNumber: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      hasAspiration: ['', Validators.required],
      aspirationType: [''],
      aspirationText: ['']
    });

    // Dynamic validation
    this.profileForm.get('aspirationType')?.valueChanges.subscribe(val => {
      if (val === 'Other') {
        this.profileForm.get('aspirationText')?.setValidators([Validators.required]);
      } else {
        this.profileForm.get('aspirationText')?.clearValidators();
      }
      this.profileForm.get('aspirationText')?.updateValueAndValidity();
    });
  }


  loadUserProfile() {
    this.userProfileService.getProfile(this.userId).subscribe({
      next: (profile) => {
        if (profile) {
          this.profileForm.patchValue({
            countryCode: '+91',
            phoneNumber: profile.phoneNumber.substring(3),
            email: profile.email,
            dateOfBirth: profile.dob,
            gender: profile.gender,
            hasAspiration: profile.hasAspiration,
            aspirationType: profile.aspirationType,
            aspirationText: profile.aspirationText
          });
          this.isProfileCompleted = this.profileForm.valid;
        }
        this.profileLoaded = true;
      },
      error: () => {
        this.profileLoaded = true;
      }
    });
  }


  saveProfile() {
    if (this.profileForm.invalid) {
      alert("Please fill all required fields.");
      return;
    }

    const dto = {
      userId: this.userId,
      phoneNumber: this.profileForm.value.countryCode + this.profileForm.value.phoneNumber,
      email: this.profileForm.value.email,
      dob: this.profileForm.value.dateOfBirth,
      gender: this.profileForm.value.gender,
      hasAspiration: this.profileForm.value.hasAspiration,
      aspirationType: this.profileForm.value.hasAspiration ? this.profileForm.value.aspirationType : null,
      aspirationText:
        this.profileForm.value.hasAspiration && this.profileForm.value.aspirationType === 'Other'
          ? this.profileForm.value.aspirationText
          : null
    };

    this.userProfileService.saveProfile(dto).subscribe({
      next: () => {
        this.isProfileCompleted = true;
        alert("Profile saved successfully!");
      },
      error: () => {
        alert("Failed to save profile.");
      }
    });
  }



  getAllQuestionGroup(assignmentId: number) {
    console.log("Profile completed or not ::::", this.isProfileCompleted)
    if (!assignmentId) {
      alert("No assignment available to start.");
      return;
    }

    sessionStorage.setItem('assignment_id', assignmentId.toString());
    this.loggingService.logDebug("redirecting to assessment page")
    this.tenantRouter.navigate('assessment');
  }

  logout() {
    this.authService.logout();
    this.tenantRouter.logoutRedirect();
  }

  ngOnDestroy(): void {
    this.assignmentListSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
  }
}
